import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import {
  getSlotsDefinitions,
  postSlotsDefinition,
  putSlotsDefinition,
  deleteSlotsDefinition,
} from "../../api/slotsDefinitionsAPI";
import { checkSlotDefinitionUsageInNodes } from "../../api/nodeSlotDefinitionAPI";
import { getSlots, postSlots } from "../../api/slotsAPI";
import { getStockCards, postStockCard } from "../../api/stockCardsAPI";

export default function SlotConfigGrid({ onConfigSelect, selectedConfig }) {
  const { t } = useTranslation();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const open = Boolean(anchorEl);

  // Dialog states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openCopyDialog, setOpenCopyDialog] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    description: "",
    originalId: null, // Kopyalama için orijinal ID
  });

  // Delete state - to store the row being deleted
  const [deleteRowData, setDeleteRowData] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // Toast/Snackbar state
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success", // success, error, warning, info
  });

  const [showInfoBox, setShowInfoBox] = useState(true);

  useEffect(() => {
    const fetchSlotDefinitions = async () => {
      try {
        setLoading(true);
        const data = await getSlotsDefinitions();

        // API'den gelen veriyi DataGrid formatına dönüştür
        const formattedRows = data.map((item) => ({
          id: item.idslotDefinitionTable,
          slotConfigName: item.name,
          slotConfigDescription: item.description,
        }));

        setRows(formattedRows);
      } catch (error) {
        console.error("Error fetching slot definitions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSlotDefinitions();
  }, []);

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleEdit = () => {
    setFormData({
      id: selectedRow.id,
      name: selectedRow.slotConfigName,
      description: selectedRow.slotConfigDescription,
    });
    setOpenEditDialog(true);
    handleMenuClose();
  };

  const handleDelete = () => {
    setDeleteRowData({
      id: selectedRow.id,
      name: selectedRow.slotConfigName,
    });
    setDeleteConfirmText("");
    setOpenDeleteDialog(true);
    handleMenuClose();
  };

  const handleCopy = () => {
    setFormData({
      id: null,
      name: selectedRow.slotConfigName + " (Kopya)",
      description: selectedRow.slotConfigDescription,
      originalId: selectedRow.id, // Orijinal ID'yi sakla
    });
    setOpenCopyDialog(true);
    handleMenuClose();
  };

  const handleAddClick = () => {
    setFormData({
      id: null,
      name: "",
      description: "",
      originalId: null,
    });
    setOpenAddDialog(true);
  };

  const handleAddSubmit = async () => {
    try {
      setLoading(true);
      await postSlotsDefinition(formData.name, formData.description);
      const data = await getSlotsDefinitions();
      const formattedRows = data.map((item) => ({
        id: item.idslotDefinitionTable,
        slotConfigName: item.name,
        slotConfigDescription: item.description,
      }));
      setRows(formattedRows);
      setOpenAddDialog(false);
      setToast({
        open: true,
        message: t("stocks.slotConfigGrid.addSuccess"),
        severity: "success",
      });
    } catch (error) {
      console.error("Error adding slot definition:", error);
      setToast({
        open: true,
        message: t("stocks.slotConfigGrid.addError") + ": " + error.message,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopySubmit = async () => {
    try {
      setLoading(true);

      // 1. Yeni slot definition oluştur
      await postSlotsDefinition(formData.name, formData.description);

      // API response'tan ID almak yerine, listeyi yenileyerek yeni oluşturulan definition'ı bul
      const allDefinitions = await getSlotsDefinitions();
      const newestDefinition = allDefinitions.find(def => def.name === formData.name);

      let newSlotDefinitionId;
      if (newestDefinition) {
        newSlotDefinitionId = newestDefinition.idslotDefinitionTable;
      } else {
        throw new Error("Yeni slot definition ID'si alınamadı");
      }

      // 2. Orijinal slot definition'a ait tüm slotları al
      const allSlots = await getSlots();
      const originalSlots = allSlots.filter(
        (slot) => slot.slotDefinitionTable_idslotDefinitionTable === formData.originalId
      );

      // 3. Tüm stock card'ları al
      const allStockCards = await getStockCards();

      // 4. Orijinal slot'larda kullanılan benzersiz stock ID'leri bul
      const usedStockIds = [...new Set(originalSlots.map(slot => slot.stockTable_idStockTable))];

      // 5. Her bir stock card'ı kopyala ve mapping oluştur (eski ID -> yeni ID)
      const stockIdMapping = {};
      for (const stockId of usedStockIds) {
        const originalStock = allStockCards.find(stock => stock.idstockTable === stockId);
        if (originalStock) {
          try {
            // Yeni stock card oluştur
            const newStockResponse = await postStockCard(
              originalStock.stockName + " (Kopya)",
              originalStock.stockPrice,
              originalStock.customerCode
            );

            // Yeni stock ID'yi mapping'e ekle
            let newStockId;
            if (newStockResponse && newStockResponse.idstockTable) {
              newStockId = newStockResponse.idstockTable;
            } else {
              // Response'ta ID yoksa, yeni oluşturulan stock'u bul
              const updatedStocks = await getStockCards();
              const newStock = updatedStocks.find(s =>
                s.stockName === originalStock.stockName + " (Kopya)" &&
                s.stockPrice === originalStock.stockPrice
              );
              if (newStock) {
                newStockId = newStock.idstockTable;
              }
            }

            if (newStockId) {
              stockIdMapping[stockId] = newStockId;
            }
          } catch (stockError) {
            console.error(`Stock ${originalStock.stockName} kopyalanırken hata:`, stockError);
            // Hata olursa, orijinal stock ID'yi kullan
            stockIdMapping[stockId] = stockId;
          }
        }
      }

      // 6. Her bir slotu yeni slot definition'a kopyala (yeni stock ID'lerle)
      let copiedCount = 0;
      for (const originalSlot of originalSlots) {
        try {
          const newStockId = stockIdMapping[originalSlot.stockTable_idStockTable] || originalSlot.stockTable_idStockTable;

          const newSlotData = {
            slotNo: originalSlot.slotNo,
            itemCount: originalSlot.itemCount,
            priceOverride: originalSlot.priceOverride,
            slotPrice: originalSlot.slotPrice,
            slotDefinitionTable_idslotDefinitionTable: newSlotDefinitionId,
            stockTable_idStockTable: newStockId,
          };
          await postSlots(newSlotData);
          copiedCount++;
        } catch (slotError) {
          console.error(`Slot ${originalSlot.slotNo} kopyalanırken hata:`, slotError);
          // Devam et, diğer slotları kopyalamaya çalış
        }
      }

      // 7. Slot definitions listesini yenile
      const data = await getSlotsDefinitions();
      const formattedRows = data.map((item) => ({
        id: item.idslotDefinitionTable,
        slotConfigName: item.name,
        slotConfigDescription: item.description,
      }));
      setRows(formattedRows);
      setOpenCopyDialog(false);

      // 8. Başarı mesajı göster
      const copiedStockCount = Object.keys(stockIdMapping).length;
      setToast({
        open: true,
        message: t("stocks.slotConfigGrid.copySuccess") + ` (${copiedCount} slot, ${copiedStockCount} stok kopyalandı)`,
        severity: copiedCount === originalSlots.length ? "success" : "warning",
      });
    } catch (error) {
      console.error("Error copying slot definition:", error);
      setToast({
        open: true,
        message: t("stocks.slotConfigGrid.copyError") + ": " + error.message,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async () => {
    try {
      setLoading(true);
      await putSlotsDefinition(formData.id, formData.name, formData.description);
      const data = await getSlotsDefinitions();
      const formattedRows = data.map((item) => ({
        id: item.idslotDefinitionTable,
        slotConfigName: item.name,
        slotConfigDescription: item.description,
      }));
      setRows(formattedRows);
      setOpenEditDialog(false);
      setToast({
        open: true,
        message: t("stocks.slotConfigGrid.editSuccess"),
        severity: "success",
      });
    } catch (error) {
      console.error("Error updating slot definition:", error);
      setToast({
        open: true,
        message: t("stocks.slotConfigGrid.editError") + ": " + error.message,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    // Check if the entered text matches the slot name
    if (deleteConfirmText !== deleteRowData.name) {
      setToast({
        open: true,
        message: t("stocks.slotConfigGrid.deleteNameMismatch"),
        severity: "error",
      });
      return;
    }

    try {
      setLoading(true);

      // Önce bu slot tanımının herhangi bir node'da kullanılıp kullanılmadığını kontrol et
      const usageCheck = await checkSlotDefinitionUsageInNodes(deleteRowData.id);

      if (usageCheck.isUsed) {
        // Slot tanımı node'larda kullanılıyor, silme işlemini engelle
        const nodeInfo = usageCheck.nodes
          .map((node) => `Node ID: ${node.nodeId}`)
          .join(", ");

        setToast({
          open: true,
          message: `${t("stocks.slotConfigGrid.deleteError")}: Bu slot tanımı ${usageCheck.count} node'da kullanılıyor. Önce bu node'lardan kaldırılmalıdır. (${nodeInfo})`,
          severity: "error",
        });

        // Close dialog and reset state
        setOpenDeleteDialog(false);
        setDeleteRowData(null);
        setDeleteConfirmText("");
        setLoading(false);
        return;
      }

      // Slot tanımı kullanılmıyorsa silme işlemine devam et
      await deleteSlotsDefinition(deleteRowData.id);

      // Refresh the list
      const data = await getSlotsDefinitions();
      const formattedRows = data.map((item) => ({
        id: item.idslotDefinitionTable,
        slotConfigName: item.name,
        slotConfigDescription: item.description,
      }));
      setRows(formattedRows);

      // Close dialog and reset state
      setOpenDeleteDialog(false);
      setDeleteRowData(null);
      setDeleteConfirmText("");

      // Show success toast
      setToast({
        open: true,
        message: t("stocks.slotConfigGrid.deleteSuccess"),
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting slot definition:", error);
      setToast({
        open: true,
        message: t("stocks.slotConfigGrid.deleteError") + ": " + error.message,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleRowClick = (params) => {
    onConfigSelect({
      id: params.row.id,
      name: params.row.slotConfigName,
      description: params.row.slotConfigDescription,
    });
  };

  const columns = [
    {
      field: "actions",
      headerName: "",
      width: 70,
      flex: 0.3,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              handleMenuClick(event, params.row);
            }}
          >
            <MoreHorizIcon />
          </IconButton>
        );
      },
    },
    {
      field: "slotConfigName",
      headerName: t("stocks.slotConfigGrid.slotConfigName"),
      width: 200,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "slotConfigDescription",
      headerName: t("stocks.slotConfigGrid.slotConfigDescription"),
      width: 300,
      flex: 1.5,
      headerAlign: "center",
      align: "center",
    },
  ];

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      <Stack direction="row" spacing={2} sx={{ mb: 2, ml: 2, alignItems: "center" }}>
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          {t("stocks.slotConfigGrid.title")}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
        >
          {t("common.add")}
        </Button>
      </Stack>

      {/* Usage Instructions */}
      {showInfoBox && (
        <Box
          sx={{
            mx: 2,
            mb: 2,
            p: 2,
            backgroundColor: "info.light",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "info.main",
            position: "relative"
          }}
        >
          <IconButton
            size="small"
            onClick={() => setShowInfoBox(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "info.dark"
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1, pr: 4 }}>
            {t("stocks.slotConfigGrid.howToUseTitle")}
          </Typography>
          <Typography variant="body2">
            {t("stocks.slotConfigGrid.howToUseDescription")}
          </Typography>
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 600 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          onRowClick={handleRowClick}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          slots={{
            noRowsOverlay: () => (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  gap: 2,
                  textAlign: "center",
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  {t("stocks.slotConfigGrid.noData")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("stocks.slotConfigGrid.noDataDescription")}
                </Typography>
              </Box>
            ),
          }}
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
            },
            "& .MuiDataGrid-row": {
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            },
            "& .MuiDataGrid-row.Mui-selected": {
              backgroundColor: "primary.light",
              "&:hover": {
                backgroundColor: "primary.light",
              },
            },
          }}
          getRowClassName={(params) =>
            selectedConfig?.id === params.row.id ? "Mui-selected" : ""
          }
        />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1, fontSize: 20 }} />
          {t("common.edit")}
        </MenuItem>
        <MenuItem onClick={handleCopy} disabled>
          <ContentCopyIcon sx={{ mr: 1, fontSize: 20 }} />
          {t("common.copy")}
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
          {t("common.delete")}
        </MenuItem>
      </Menu>

      {/* Add Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>{t("stocks.slotConfigGrid.addSlotDefinition")}</DialogTitle>
        <DialogContent dividers sx={{ minHeight: 200 }}>
          <Box sx={{ py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label={t("stocks.slotConfigGrid.name")}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label={t("stocks.slotConfigGrid.description")}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              required
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleAddSubmit} variant="contained" color="primary">
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>{t("stocks.slotConfigGrid.editSlotDefinition")}</DialogTitle>
        <DialogContent dividers sx={{ minHeight: 200 }}>
          <Box sx={{ py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label={t("stocks.slotConfigGrid.name")}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              multiline
              required
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label={t("stocks.slotConfigGrid.description")}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              required
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleEditSubmit} variant="contained" color="primary">
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Dialog */}
      <Dialog
        open={openCopyDialog}
        onClose={() => setOpenCopyDialog(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>{t("stocks.slotConfigGrid.copySlotDefinition")}</DialogTitle>
        <DialogContent dividers sx={{ minHeight: 200 }}>
          <Box sx={{ py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label={t("stocks.slotConfigGrid.name")}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label={t("stocks.slotConfigGrid.description")}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              required
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCopyDialog(false)}>{t("common.cancel")}</Button>
          <Button onClick={handleCopySubmit} variant="contained" color="primary">
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("stocks.slotConfigGrid.deleteSlotDefinition")}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography>
              {t("stocks.slotConfigGrid.deleteConfirmPrompt")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("stocks.slotConfigGrid.slotNameToDelete")}: <strong>{deleteRowData?.name}</strong>
            </Typography>
            <TextField
              label={t("stocks.slotConfigGrid.enterSlotName")}
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              fullWidth
              placeholder={deleteRowData?.name}
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>{t("common.cancel")}</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleteConfirmText !== deleteRowData?.name}
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast/Snackbar */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
